import { supabase } from "../lib/supabaseClient";
import { throwIfError } from "../lib/apiError";
import { requireProfile, resolveClinicId } from "../lib/sessionUser";
import { assertImageFile, fetchAsDataUrl, fileExtension, publicObjectUrl } from "./storageService";
import { incrementProtocolNumber } from "../utils/protocolPreferences";

const CLINIC_COLUMNS =
  "id, name, address, phone, email, logo_path, description, clinic_mode, is_active, created_at, enable_weight, enable_blood_pressure, enable_temperature, enable_heart_rate, use_protocol_number, allow_nurse_protocol, allow_doctor_protocol, theme_id";

const WATERMARK_COLUMN = "show_pdf_watermark";
const PROTOCOL_SEQ_COLUMN = "next_protocol_number";
let watermarkColumnAvailable = true;
let protocolSeqColumnAvailable = true;

function watermarkStorageKey(clinicId) {
  return `clinicops_show_pdf_watermark_${clinicId}`;
}

function readStoredWatermark(clinicId) {
  if (!clinicId || typeof localStorage === "undefined") return true;
  try {
    const stored = localStorage.getItem(watermarkStorageKey(clinicId));
    if (stored == null) return true;
    return stored !== "false";
  } catch {
    return true;
  }
}

function writeStoredWatermark(clinicId, value) {
  if (!clinicId || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(watermarkStorageKey(clinicId), String(!!value));
  } catch {
    // The saved clinic row still carries the choice when the column exists.
  }
}

function protocolStorageKey(clinicId) {
  return `clinicops_next_protocol_${clinicId}`;
}

function readStoredProtocol(clinicId) {
  if (!clinicId || typeof localStorage === "undefined") return "";
  try {
    return localStorage.getItem(protocolStorageKey(clinicId)) || "";
  } catch {
    return "";
  }
}

function writeStoredProtocol(clinicId, value) {
  if (!clinicId || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(protocolStorageKey(clinicId), String(value ?? "").trim());
  } catch {
    // The clinic row keeps the counter when the column exists.
  }
}

function missingOptionalColumn(error) {
  const message = `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`;
  let matched = false;
  if (watermarkColumnAvailable && /show_pdf_watermark/i.test(message)) {
    watermarkColumnAvailable = false;
    matched = true;
  }
  if (protocolSeqColumnAvailable && /next_protocol_number/i.test(message)) {
    protocolSeqColumnAvailable = false;
    matched = true;
  }
  return matched;
}

function clinicSelectColumns() {
  const extra = [];
  if (watermarkColumnAvailable) extra.push(WATERMARK_COLUMN);
  if (protocolSeqColumnAvailable) extra.push(PROTOCOL_SEQ_COLUMN);
  return extra.length ? `${CLINIC_COLUMNS}, ${extra.join(", ")}` : CLINIC_COLUMNS;
}

function withWatermark(clinic, clinicId, row) {
  if (!clinic) return clinic;
  const fromColumn = row && Object.prototype.hasOwnProperty.call(row, WATERMARK_COLUMN) ? row[WATERMARK_COLUMN] : null;
  return {
    ...clinic,
    showPdfWatermark: fromColumn == null ? readStoredWatermark(clinicId) : !!fromColumn,
  };
}

export function mapClinic(row) {
  if (!row) return null;
  return {
    id: row.id,
    name: row.name,
    address: row.address,
    phone: row.phone,
    email: row.email,
    logoUrl: publicObjectUrl("clinic-assets", row.logo_path),
    description: row.description,
    clinicMode: row.clinic_mode,
    isActive: row.is_active,
    createdAt: row.created_at,
    vitalPreferences: {
      enableWeight: row.enable_weight,
      enableBloodPressure: row.enable_blood_pressure,
      enableTemperature: row.enable_temperature,
      enableHeartRate: row.enable_heart_rate,
    },
    protocolPreferences: {
      useProtocolNumber: row.use_protocol_number,
      allowNurseToSet: row.allow_nurse_protocol,
      allowDoctorToSet: row.allow_doctor_protocol,
      nextProtocolNumber:
        row && Object.prototype.hasOwnProperty.call(row, PROTOCOL_SEQ_COLUMN)
          ? String(row[PROTOCOL_SEQ_COLUMN] ?? "").trim()
          : readStoredProtocol(row?.id),
    },
    colorThemePreferences: {
      themeId: row.theme_id || "default",
    },
  };
}

export async function getClinicProfile() {
  const clinicId = await resolveClinicId();
  const profile = await requireProfile();
  if (profile.role === "SuperAdmin" && !profile.clinic_id && clinicId) {
    // SuperAdmin reads the clinic they are operating on.
  }
  let { data, error } = await supabase.from("clinics").select(clinicSelectColumns()).eq("id", clinicId).maybeSingle();
  while (error && missingOptionalColumn(error)) {
    ({ data, error } = await supabase.from("clinics").select(clinicSelectColumns()).eq("id", clinicId).maybeSingle());
  }
  throwIfError(error, "Dështoi ngarkimi i profilit të klinikës.");
  if (!data) {
    const err = new Error("Profili i klinikës nuk u gjet.");
    err.response = { data: { message: err.message }, status: 404 };
    throw err;
  }
  return withWatermark(mapClinic(data), clinicId, data);
}

export async function updateClinicProfile(body) {
  const clinicId = await resolveClinicId();
  const payload = {};
  if (body.name != null) payload.name = body.name;
  if (body.address != null) payload.address = body.address;
  if (body.phone != null) payload.phone = body.phone;
  if (body.logoUrl != null) payload.logo_path = body.logoUrl;
  if (body.description != null) payload.description = body.description;
  if (body.vitalPreferences != null) {
    const v = body.vitalPreferences;
    payload.enable_weight = !!(v.enableWeight ?? v.EnableWeight);
    payload.enable_blood_pressure = !!(v.enableBloodPressure ?? v.EnableBloodPressure);
    payload.enable_temperature = !!(v.enableTemperature ?? v.EnableTemperature);
    payload.enable_heart_rate = !!(v.enableHeartRate ?? v.EnableHeartRate);
  }
  if (body.protocolPreferences != null) {
    const p = body.protocolPreferences;
    payload.use_protocol_number = !!(p.useProtocolNumber ?? p.UseProtocolNumber);
    payload.allow_nurse_protocol = p.allowNurseToSet ?? p.AllowNurseToSet ?? true;
    payload.allow_doctor_protocol = p.allowDoctorToSet ?? p.AllowDoctorToSet ?? true;
    const nextNumber = String(p.nextProtocolNumber ?? p.NextProtocolNumber ?? "").trim();
    payload.next_protocol_number = nextNumber;
    writeStoredProtocol(clinicId, nextNumber);
  }
  if (body.colorThemePreferences != null) {
    const theme = body.colorThemePreferences.themeId ?? body.colorThemePreferences.ThemeId;
    if (theme != null) payload.theme_id = theme;
  }
  if (body.showPdfWatermark != null) {
    payload.show_pdf_watermark = !!body.showPdfWatermark;
    writeStoredWatermark(clinicId, payload.show_pdf_watermark);
  }

  let { data, error } = await supabase
    .from("clinics")
    .update(payload)
    .eq("id", clinicId)
    .select(clinicSelectColumns())
    .single();
  while (error && missingOptionalColumn(error)) {
    if (!watermarkColumnAvailable) delete payload.show_pdf_watermark;
    if (!protocolSeqColumnAvailable) delete payload.next_protocol_number;
    if (Object.keys(payload).length === 0) {
      const current = await getClinicProfile();
      if (body.showPdfWatermark != null) current.showPdfWatermark = !!body.showPdfWatermark;
      if (body.protocolPreferences != null) {
        current.protocolPreferences = {
          ...current.protocolPreferences,
          nextProtocolNumber: String(
            body.protocolPreferences.nextProtocolNumber ?? body.protocolPreferences.NextProtocolNumber ?? ""
          ).trim(),
        };
      }
      return current;
    }
    ({ data, error } = await supabase
      .from("clinics")
      .update(payload)
      .eq("id", clinicId)
      .select(clinicSelectColumns())
      .single());
  }
  throwIfError(error, "Dështoi përditësimi i profilit të klinikës.");
  const clinic = withWatermark(mapClinic(data), clinicId, data);
  if (body.showPdfWatermark != null) clinic.showPdfWatermark = !!body.showPdfWatermark;
  if (body.protocolPreferences != null && !protocolSeqColumnAvailable) {
    clinic.protocolPreferences = {
      ...clinic.protocolPreferences,
      nextProtocolNumber: String(
        body.protocolPreferences.nextProtocolNumber ?? body.protocolPreferences.NextProtocolNumber ?? ""
      ).trim(),
    };
  }
  return clinic;
}

/** Give the next protocol number to a new case, then store the following one. */
export async function takeNextProtocolNumber() {
  const clinicId = await resolveClinicId();
  const profile = await getClinicProfile();
  const prefs = profile?.protocolPreferences;
  if (!prefs?.useProtocolNumber) return null;
  const current = String(prefs.nextProtocolNumber || "").trim();
  if (!current || !/\d$/.test(current)) return null;
  const following = incrementProtocolNumber(current);
  writeStoredProtocol(clinicId, following);
  if (protocolSeqColumnAvailable) {
    const { error } = await supabase
      .from("clinics")
      .update({ next_protocol_number: following })
      .eq("id", clinicId);
    if (error && missingOptionalColumn(error)) {
      // The browser copy already holds the next number.
    } else {
      throwIfError(error, "Dështoi përditësimi i numrit të protokollit.");
    }
  }
  return current;
}

export async function uploadClinicLogo(file) {
  assertImageFile(file);
  const clinicId = await resolveClinicId();
  const path = `${clinicId}/logo.${fileExtension(file)}`;
  const { error: uploadError } = await supabase.storage.from("clinic-assets").upload(path, file, {
    upsert: true,
    contentType: file.type || "image/png",
    cacheControl: "0",
  });
  throwIfError(uploadError, "Ngarkimi i logos dështoi.");
  const { data, error } = await supabase
    .from("clinics")
    .update({ logo_path: path })
    .eq("id", clinicId)
    .select(clinicSelectColumns())
    .single();
  if (error && missingOptionalColumn(error)) {
    const retry = await supabase
      .from("clinics")
      .update({ logo_path: path })
      .eq("id", clinicId)
      .select(clinicSelectColumns())
      .single();
    throwIfError(retry.error, "Ngarkimi i logos dështoi.");
    return withWatermark(mapClinic(retry.data), clinicId, retry.data);
  }
  throwIfError(error, "Ngarkimi i logos dështoi.");
  return withWatermark(mapClinic(data), clinicId, data);
}

export function getLogoFullUrl(logoUrl) {
  return publicObjectUrl("clinic-assets", logoUrl);
}

export async function getLogoAsBase64(logoUrl) {
  return fetchAsDataUrl(logoUrl, "clinic-assets");
}
